import { ArrowLeft, Gavel, KeyRound, Coins, Loader2, Camera } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import ImageGridSection from "./ImageGridSection";

/**
 * AuctionRentalBasicsCard — Jun-2026 (User-Wunsch).
 *
 * Schlanke Eingabe für Auktion / Sharing-Vermietung. Wenn „Auktion" oder
 * „Sharing/Vermietung" gewählt ist, blendet CreateListingPage das komplette
 * Fahrzeug-Stammdaten-Formular aus und zeigt NUR diese Karte: ein prominenter
 * Button zurück zum „Klassischen Inserat mit Festpreis", die nötigen Eckdaten
 * (Titel, Marke, Modell, Erstzulassung/Baujahr, Standort) und die Bilder.
 *
 * Reines Präsentations-Element — Setter kommen via Props vom Parent.
 */
export default function AuctionRentalBasicsCard({
    form,
    setForm,
    update,
    isMoto,
    mode,            // "auction" | "rental" | "pawn"
    onBackToFixed,
    footerActionsRef,
    submitting,
    isEdit,
    showEckdaten = true,   // Jun-2026: bei Auktionen mit Bestandsfahrzeug ausgeblendet
}) {
    const isAuction = mode === "auction";
    const isPawn = mode === "pawn";
    const HeadIcon = isAuction ? Gavel : isPawn ? Coins : KeyRound;
    const headLabel = isAuction
        ? "Auktion — nur Auktionsdaten"
        : isPawn
            ? "Pfand / Refinanzieren — nur Pfanddaten"
            : "Sharing / Vermietung — nur Mietdaten";
    return (
        <div className="space-y-6" data-testid="special-mode-block">
            {/* Kopf mit „Zurück zu Festpreis"-Button (ganz oben im Block, User-Wunsch) */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-[#FFCC00]/20 border-2 border-[#111827] rounded-sm p-4">
                <div className="flex items-center gap-2 font-display font-black text-[#111827]">
                    <HeadIcon className="w-5 h-5" />
                    {headLabel}
                </div>
                <button
                    type="button"
                    onClick={onBackToFixed}
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm text-[11px] font-bold uppercase tracking-wider bg-white border-2 border-[#111827] text-[#111827] hover:bg-[#111827] hover:text-white transition-colors"
                    data-testid="back-to-fixed-btn"
                    title="Zurück zum klassischen Inserat mit Festpreis — das vollständige Fahrzeug-Formular wird wieder angezeigt."
                >
                    <ArrowLeft className="w-4 h-4" /> Klassisches Inserat mit Festpreis
                </button>
            </div>

            {/* Eckdaten — das absolute Minimum, damit das Inserat eindeutig ist. */}
            {showEckdaten && (
            <div className="bg-white border border-gray-200 rounded-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="special-basics-card">
                <h2 className="md:col-span-2 font-display text-base font-bold text-[#111827]">
                    Eckdaten {isAuction ? "der Auktion" : isPawn ? "des Pfand-Fahrzeugs" : "der Vermietung"}
                </h2>
                <div className="md:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Titel *</Label>
                    <Input
                        value={form.title}
                        onChange={(e) => update("title", e.target.value)}
                        placeholder="z. B. Vespa GTS 300 — Top-Zustand"
                        required
                        className="mt-1.5 rounded-sm border-2 border-red-400"
                        data-testid="form-title"
                    />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Marke *</Label>
                    <Input
                        value={form.brand}
                        onChange={(e) => update("brand", e.target.value)}
                        required
                        className="mt-1.5 rounded-sm border-2 border-red-400"
                        data-testid="form-brand"
                    />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Modell *</Label>
                    <Input
                        value={form.model}
                        onChange={(e) => update("model", e.target.value)}
                        required
                        className="mt-1.5 rounded-sm border-2 border-red-400"
                        data-testid="form-model"
                    />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">
                        {isMoto ? "Erstzulassung (B)" : "Baujahr"}
                    </Label>
                    {isMoto ? (
                        <Input
                            type="date"
                            value={form.first_registration_date}
                            onChange={(e) => {
                                const v = e.target.value;
                                setForm((p) => ({ ...p, first_registration_date: v, year: v ? parseInt(v.slice(0, 4), 10) : p.year }));
                            }}
                            max={new Date().toISOString().slice(0, 10)}
                            className="mt-1.5 rounded-sm"
                            data-testid="form-erstzulassung"
                        />
                    ) : (
                        <Input
                            type="number"
                            value={form.year}
                            onChange={(e) => update("year", e.target.value)}
                            className="mt-1.5 rounded-sm font-mono-num"
                            data-testid="form-year"
                        />
                    )}
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Standort</Label>
                    <Input
                        value={form.location}
                        onChange={(e) => update("location", e.target.value)}
                        placeholder="PLZ / Ort"
                        className="mt-1.5 rounded-sm"
                        data-testid="form-location"
                    />
                </div>
            </div>
            )}

            {/* Bilder — gleiche Komponente wie im klassischen Inserat. */}
            {/* Jun-2026 (User-Wunsch) — bei Auktionen IMMER ein prominenter Foto-Block,
                auch wenn ein Bestandsfahrzeug gewählt wurde (Fotos können ergänzt werden). */}
            {isAuction && !showEckdaten && (
                <div className="bg-[#FFCC00]/15 border-2 border-[#111827] rounded-sm p-4" data-testid="auction-photo-block">
                    <div className="flex items-center gap-2 font-display font-black text-[#111827] mb-1">
                        <Camera className="w-5 h-5" /> Fotos der Auktion
                    </div>
                    <p className="text-xs text-gray-700 mb-4">
                        Aussagekräftige Fotos steigern Gebote deutlich. Bilder des gewählten Fahrzeugs werden
                        übernommen — Sie können hier jederzeit weitere Fotos hinzufügen oder ersetzen.
                    </p>
                    <ImageGridSection form={form} setForm={setForm} actionsRef={footerActionsRef} />
                </div>
            )}
            {showEckdaten && (
                <ImageGridSection form={form} setForm={setForm} actionsRef={footerActionsRef} />
            )}

            {/* Veröffentlichen */}
            <div className="flex justify-end" data-testid="special-publish-section">
                <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm px-8"
                    data-testid="submit-listing-btn"
                >
                    {submitting
                        ? "Wird gespeichert…"
                        : isEdit
                            ? "Änderungen speichern"
                            : isAuction ? "Auktion starten" : isPawn ? "Pfand-Inserat veröffentlichen" : "Vermietung veröffentlichen"}
                    {submitting && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
                </Button>
            </div>
        </div>
    );
}
